import { FormEvent, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import type { ContactKind, ContactPoint, Me, PendingChange } from '../domain/account';
import { announceAccountChanged } from '../hooks/useMe';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import type { AccountRepository } from '../repositories/Repositories';
import ConfirmDialog from './ConfirmDialog';
import { ActionError, ErrorState } from './PageStates';

/** The 011 code steps, once per kind: verifying an email and a phone are the same experience. */
function verificationFor(account: AccountRepository, kind: ContactKind) {
  return kind === 'email'
    ? {
        start: (value: string) => account.startEmailChange(value),
        confirm: (code: string) => account.confirmEmailChange(code),
        resend: () => account.resendEmailCode(),
        cancel: () => account.cancelEmailChange(),
      }
    : {
        start: (value: string) => account.startPhoneChange(value),
        confirm: (code: string) => account.confirmPhoneChange(code),
        resend: () => account.resendPhoneCode(),
        cancel: () => account.cancelPhoneChange(),
      };
}

const COPY: Record<
  ContactKind,
  { title: string; add: string; input: string; type: 'email' | 'tel'; placeholder: string }
> = {
  email: {
    title: 'Email',
    add: 'Add email',
    input: 'New email',
    type: 'email',
    placeholder: 'you@example.com',
  },
  phone: {
    title: 'Phone',
    add: 'Add phone number',
    input: 'New phone',
    type: 'tel',
    placeholder: '+1 425 232 4426',
  },
};

export const ONLY_VERIFIED_WARNING =
  "You won't be able to invite, post or message until you verify another email or phone.";

/**
 * My emails and phones (019 R18): each verified or waiting, findable or not,
 * and removable; adding one sends a code, and verifying it leaves the others
 * as they are. The code in flight comes from my account, so a reload — or
 * another device — picks it up where it was.
 */
export default function ContactPointsSection({
  profile,
  onProfile,
  onPending,
  onRefresh,
}: {
  profile: Me;
  onProfile: (me: Me) => void;
  onPending: (kind: ContactKind, pending: PendingChange) => void;
  onRefresh: () => Promise<void>;
}) {
  const { account } = useRepositories();
  const points = useLoad<ContactPoint[]>(() => account.contactPoints(), 'contact-points');
  const [removing, setRemoving] = useState<ContactPoint | null>(null);
  const [error, setError] = useState<unknown>(null);
  const reload = points.refresh;

  const changed = useCallback(async () => {
    reload();
    announceAccountChanged();
  }, [reload]);

  const toggle = async (point: ContactPoint) => {
    setError(null);
    try {
      await account.updateContactPoint(point.id, !point.discoverable);
    } catch (e) {
      setError(e);
    }
    reload();
  };

  const remove = async (point: ContactPoint) => {
    setError(null);
    try {
      await account.removeContactPoint(point.id);
      await onRefresh();
      await changed();
    } catch (e) {
      setError(e);
    }
  };

  const verify = async (point: ContactPoint) => {
    setError(null);
    try {
      onPending(point.kind, await verificationFor(account, point.kind).start(point.value));
    } catch (e) {
      setError(e);
    }
  };

  const verifiedCount =
    points.status === 'ready' ? points.data.filter((p) => p.verifiedAt).length : 0;
  const pendingFor = (kind: ContactKind) =>
    kind === 'email' ? profile.pendingEmailChange : profile.pendingPhoneChange;

  return (
    <Card
      id="emails-and-phones"
      className="mb-6 scroll-mt-24"
      role="region"
      aria-labelledby="contact-points-heading"
    >
      <CardHeader>
        <CardTitle id="contact-points-heading">Emails and phones</CardTitle>
        <p className="text-sm text-muted-foreground">
          Only you see them. People who have one in their contacts can find you by it, unless you
          turn that off.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {points.status === 'loading' ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading…
          </p>
        ) : points.status === 'error' ? (
          <ErrorState message="Couldn't load your emails and phones." onRetry={points.reload} />
        ) : points.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No emails or phone numbers yet.</p>
        ) : (
          <ul aria-label="Your emails and phones" className="space-y-2">
            {points.data.map((p) => {
              const inFlight = pendingFor(p.kind)?.newValue === p.value;
              return (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center gap-3 rounded-2xl bg-muted px-4 py-3"
                >
                  <span className="min-w-0 font-medium tabular-nums">{p.value}</span>
                  {p.verifiedAt ? (
                    <Badge className="gap-1 border-none bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      <CheckCircle2 className="size-3.5" aria-hidden="true" />
                      Verified
                    </Badge>
                  ) : (
                    <Badge className="gap-1 border-none bg-background text-muted-foreground">
                      <Clock className="size-3.5" aria-hidden="true" />
                      Waiting for verification
                    </Badge>
                  )}
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={p.discoverable}
                        aria-label={`Find me by ${p.value}`}
                        onClick={() => toggle(p)}
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                          p.discoverable ? 'bg-primary' : 'bg-input'
                        }`}
                      >
                        <span
                          className={`inline-block size-5 rounded-full bg-background shadow transition-transform ${
                            p.discoverable ? 'translate-x-5' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                      <span aria-hidden="true">Findable</span>
                    </div>
                    {!p.verifiedAt && !inFlight && (
                      <Button size="sm" variant="secondary" onClick={() => verify(p)}>
                        Verify
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove ${p.value}`}
                      onClick={() => setRemoving(p)}
                    >
                      Remove
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {error !== null && <ActionError error={error} />}

        {(['email', 'phone'] as const).map((kind) => {
          const pending = pendingFor(kind);
          return pending ? (
            <CodeEntry
              key={kind}
              kind={kind}
              pending={pending}
              onPending={onPending}
              onProfile={(me) => {
                onProfile(me);
                changed();
              }}
              onRefresh={async () => {
                await onRefresh();
                reload();
              }}
            />
          ) : null;
        })}

        <div className="flex flex-wrap gap-2">
          {(['email', 'phone'] as const).map((kind) => (
            <AddValue
              key={kind}
              kind={kind}
              onPending={(p) => {
                onPending(kind, p);
                reload();
              }}
            />
          ))}
        </div>
      </CardContent>

      {removing && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setRemoving(null)}
          title={`Remove ${removing.value}?`}
          description={
            <div className="space-y-2">
              <p>It comes off your account, and nobody can find you by it.</p>
              {removing.verifiedAt && verifiedCount === 1 && <p>{ONLY_VERIFIED_WARNING}</p>}
            </div>
          }
          confirmLabel="Remove"
          onConfirm={() => remove(removing)}
        />
      )}
    </Card>
  );
}

/** "Add email" / "Add phone number": the value, then a code is sent. */
function AddValue({
  kind,
  onPending,
}: {
  kind: ContactKind;
  onPending: (pending: PendingChange) => void;
}) {
  const { account } = useRepositories();
  const copy = COPY[kind];
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const focusOnMount = useCallback((el: HTMLInputElement | null) => el?.focus(), []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onPending(await verificationFor(account, kind).start(draft));
      setDraft('');
      setOpen(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {copy.add}
      </Button>
    );
  }
  return (
    <form
      onSubmit={submit}
      aria-label={copy.add}
      className="w-full space-y-2 rounded-2xl bg-muted px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Input
          aria-label={copy.input}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          type={copy.type}
          inputMode={copy.type}
          placeholder={copy.placeholder}
          ref={focusOnMount}
          className="w-full flex-1 bg-background sm:w-auto"
        />
        <Button type="submit" disabled={busy || !draft.trim()}>
          Send code
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setDraft('');
            setError(null);
          }}
          disabled={busy}
        >
          Cancel
        </Button>
      </div>
      {error !== null && <ActionError error={error} />}
    </form>
  );
}

/**
 * Entering the code for the value in flight (011): Verify, Resend, Cancel.
 * Cancel stops the code; the value stays, waiting (019 Q2).
 */
function CodeEntry({
  kind,
  pending,
  onPending,
  onProfile,
  onRefresh,
}: {
  kind: ContactKind;
  pending: PendingChange;
  onPending: (kind: ContactKind, pending: PendingChange) => void;
  onProfile: (me: Me) => void;
  onRefresh: () => Promise<void>;
}) {
  const { account } = useRepositories();
  const verification = verificationFor(account, kind);
  const { title } = COPY[kind];
  const [code, setCode] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<void>, refreshOnError = false) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setError(err);
      // A rejected code can mean the challenge is gone rather than just
      // wrong: the server retires it after the last bad attempt and words it
      // the same way. Re-reading keeps the code box derived from the server.
      if (refreshOnError) await onRefresh();
    } finally {
      setBusy(false);
    }
  };

  const confirm = (e: FormEvent) => {
    e.preventDefault();
    return run(async () => {
      onProfile(await verification.confirm(code));
      setCode('');
      setError(null);
      setHint(null);
      toast(`${title} verified.`);
    }, true);
  };

  const resend = () =>
    run(async () => {
      onPending(kind, await verification.resend());
      setCode('');
      setError(null);
      setHint('We sent a new code.');
    }, true);

  const cancel = () =>
    run(async () => {
      const result = await verification.cancel();
      // `cancelled: false` with the value now verified means it landed on
      // another device a moment earlier: a state to render, not an error.
      onProfile(result.me);
      setCode('');
      setError(null);
      setHint(null);
    });

  return (
    <div className="space-y-2">
      <form
        onSubmit={confirm}
        aria-label={`${title} code`}
        className="flex flex-wrap items-center gap-3 rounded-2xl bg-secondary/40 px-4 py-3"
      >
        <span className="font-medium tabular-nums">{pending.newValue}</span>
        <Input
          aria-label={`${title} verification code`}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          placeholder="6-digit code"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          className="w-36 bg-background tracking-[0.14em]"
        />
        <Button type="submit" disabled={busy}>
          Verify
        </Button>
        <Button
          type="button"
          variant="link"
          className="text-muted-foreground underline"
          onClick={resend}
          disabled={busy}
        >
          Resend
        </Button>
        <Button type="button" variant="ghost" className="ml-auto" onClick={cancel} disabled={busy}>
          Cancel
        </Button>
      </form>
      {pending.deliveryNotice && (
        <p className="text-xs text-muted-foreground">{pending.deliveryNotice}</p>
      )}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error !== null && <ActionError error={error} />}
    </div>
  );
}
